import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import {
  getAiActivityStatus,
  AiActivityItem,
  OutputType,
} from '../endpoints/ai-activity/status_GET.schema';

// Adaptive poll intervals:
// - fast while AI is actively running
// - slower when idle
// - paused when tab is hidden
const AI_ACTIVITY_ACTIVE_POLL_INTERVAL = 5000;
const AI_ACTIVITY_IDLE_POLL_INTERVAL = 30000;

/**
 * Hook to track AI activity in a session
 * All participants can see when AI is working
 */
export const useAiActivity = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['ai', 'activity', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getAiActivityStatus({ sessionId });
    },
    enabled: !!sessionId,
    refetchInterval: (query) => {
      if (query.state.error) return false;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
      }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return false;
      }
      if (typeof document !== 'undefined' && !document.hasFocus()) {
        return AI_ACTIVITY_IDLE_POLL_INTERVAL;
      }
      const data = query.state.data as OutputType | undefined;
      return data?.isActive ? AI_ACTIVITY_ACTIVE_POLL_INTERVAL : AI_ACTIVITY_IDLE_POLL_INTERVAL;
    },
    staleTime: 10000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error instanceof Error && error.message === 'Not authenticated') {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Get the current active AI activity (if any)
 */
export const useCurrentAiActivity = (sessionId: string | undefined) => {
  const { data, isLoading, error } = useAiActivity(sessionId);

  // Find the most recent active (pending/processing) activity
  const currentActivity = data?.activities?.find(
    (a) => a.status === 'pending' || a.status === 'processing',
  );

  return {
    isActive: data?.isActive ?? false,
    currentActivity,
    recentActivities: data?.activities ?? [],
    isLoading,
    error,
  };
};

export type { AiActivityItem, OutputType as AiActivityOutput };
